import { SnakeState } from '../snake/SnakeState.js';

export const MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION = 1;

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value).sort().map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeMultiplayerPlayerSnakeChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  return value;
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function normalizePosition(value, fallback, name) {
  if (value === undefined || value === null) return { ...fallback };
  assertPlainObject(value, name);
  const result = {};
  for (const axis of ['x', 'y', 'z']) {
    const candidate = value[axis] ?? fallback[axis];
    if (!Number.isFinite(candidate)) throw new RangeError(`${name}.${axis} must be finite`);
    result[axis] = candidate;
  }
  return result;
}

function stableEntityId(sessionId, playerId) {
  const digest = checksumPayload({ sessionId, playerId }).split(':')[1];
  return `multiplayer-snake-${digest}`;
}

function spawnOptionsFor(player, session, localSnake) {
  const index = Math.max(0, player.joinOrder - 1);
  const fallback = localSnake?.position ?? { x: 0, y: 0, z: 0 };
  const position = normalizePosition(
    player.metadata?.spawnPosition,
    {
      x: fallback.x + ((index % 4) * 0.9),
      y: fallback.y,
      z: fallback.z + (Math.floor(index / 4) * 0.9),
    },
    `player ${player.playerId} spawnPosition`,
  );
  return {
    entityId: stableEntityId(session.sessionId, player.playerId),
    species: typeof player.metadata?.species === 'string' && player.metadata.species.trim()
      ? player.metadata.species.trim()
      : 'common-water-snake',
    position,
    heading: Number.isFinite(player.metadata?.heading) ? player.metadata.heading : 0,
    length: Number.isFinite(player.metadata?.length) ? player.metadata.length : 0.65,
    mass: Number.isFinite(player.metadata?.mass) ? player.metadata.mass : 0.13,
  };
}

function normalizeRecord(record, index) {
  assertPlainObject(record, `snapshot.snakes[${index}]`);
  if (typeof record.local !== 'boolean') {
    throw new TypeError(`snapshot.snakes[${index}].local must be a boolean`);
  }
  assertPlainObject(record.snake, `snapshot.snakes[${index}].snake`);
  return {
    playerId: assertNonEmptyString(record.playerId, `snapshot.snakes[${index}].playerId`),
    displayName: assertNonEmptyString(record.displayName, `snapshot.snakes[${index}].displayName`),
    role: assertNonEmptyString(record.role, `snapshot.snakes[${index}].role`),
    playerStatus: assertNonEmptyString(record.playerStatus, `snapshot.snakes[${index}].playerStatus`),
    local: record.local,
    snake: clone(record.snake),
  };
}

export class MultiplayerPlayerSnakeSystem {
  constructor({ entityWorld, traversalWorld, localSnakeState, multiplayerState }) {
    for (const [value, methods, name] of [
      [entityWorld, ['snapshot', 'restore', 'get'], 'entityWorld'],
      [traversalWorld, ['snapshot', 'restore'], 'traversalWorld'],
      [localSnakeState, ['spawn', 'step', 'snapshot', 'restore', 'reset'], 'localSnakeState'],
      [multiplayerState, ['getStatus', 'getSession', 'getPlayer', 'listPlayers', 'getInputForTick', 'getAuthority', 'claimAuthority', 'releaseAuthority', 'snapshot'], 'multiplayerState'],
    ]) {
      for (const method of methods) assertMethod(value, method, name);
    }
    this.entityWorld = entityWorld;
    this.traversalWorld = traversalWorld;
    this.localSnakeState = localSnakeState;
    this.multiplayerState = multiplayerState;
    this.remoteSnakes = new Map();
    this.localPlayerId = null;
    this.revision = 0;
    this.tick = null;
    this.lastStep = null;
  }

  #claim(playerId, entityId) {
    const authorityId = `entity:${entityId}`;
    const existing = this.multiplayerState.getAuthority(authorityId);
    if (!existing) {
      this.multiplayerState.claimAuthority({
        authorityId,
        playerId,
        kind: 'player-snake',
      });
    } else if (existing.ownerPlayerId !== playerId) {
      throw new RangeError(`Snake authority ${authorityId} belongs to another player`);
    }
  }

  #release(playerId, entityId) {
    const authorityId = `entity:${entityId}`;
    const existing = this.multiplayerState.getAuthority(authorityId);
    if (existing?.ownerPlayerId === playerId) {
      this.multiplayerState.releaseAuthority(authorityId, { playerId });
    }
  }

  #resolveLocalPlayerId(requested) {
    const session = this.multiplayerState.getSession();
    if (!session) return null;
    const candidate = requested ?? this.localPlayerId ?? session.hostPlayerId;
    const player = this.multiplayerState.getPlayer(candidate);
    if (!player || player.status === 'left') {
      throw new RangeError(`Local multiplayer player is unavailable: ${candidate}`);
    }
    return player.playerId;
  }

  sync({ localPlayerId = this.localPlayerId } = {}) {
    const status = this.multiplayerState.getStatus();
    if (!status.active || status.status !== 'running') {
      this.reset({ preserveLocalSnake: true });
      return this.getStatus();
    }
    const session = this.multiplayerState.getSession();
    const resolvedLocalId = this.#resolveLocalPlayerId(localPlayerId);
    const players = this.multiplayerState.listPlayers({ includeLeft: false });
    const activeIds = new Set(players.map((player) => player.playerId));
    const staged = [];
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld.snapshot();
    const localBackup = this.localSnakeState.snapshot();

    try {
      const localPlayer = this.multiplayerState.getPlayer(resolvedLocalId);
      let localSnake = this.localSnakeState.snapshot();
      if (!localSnake) {
        localSnake = this.localSnakeState.spawn(spawnOptionsFor(localPlayer, session, null));
      }
      this.#claim(localPlayer.playerId, localSnake.entityId);

      for (const player of players) {
        if (player.playerId === resolvedLocalId || this.remoteSnakes.has(player.playerId)) continue;
        const snakeState = new SnakeState({
          entityWorld: this.entityWorld,
          traversalWorld: this.traversalWorld,
        });
        const snake = snakeState.spawn(spawnOptionsFor(player, session, localSnake));
        this.#claim(player.playerId, snake.entityId);
        staged.push([player.playerId, snakeState]);
      }
      for (const [playerId, snakeState] of staged) this.remoteSnakes.set(playerId, snakeState);

      for (const [playerId, snakeState] of [...this.remoteSnakes.entries()]) {
        if (activeIds.has(playerId) && playerId !== resolvedLocalId) continue;
        const snake = snakeState.snapshot();
        if (snake) this.#release(playerId, snake.entityId);
        snakeState.reset({ removeEntity: true });
        this.remoteSnakes.delete(playerId);
      }

      this.localPlayerId = resolvedLocalId;
      this.revision += 1;
      return this.getStatus();
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      this.traversalWorld.restore(traversalBackup);
      for (const [, snakeState] of staged) snakeState.reset({ removeEntity: false });
      if (localBackup) this.localSnakeState.restore(localBackup);
      else this.localSnakeState.reset({ removeEntity: false });
      throw error;
    }
  }

  step({ tick, simulationTime, stepSeconds, localPlayerId = this.localPlayerId }) {
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    if (!Number.isFinite(simulationTime) || simulationTime < 0) {
      throw new RangeError('simulationTime must be a non-negative finite number');
    }
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    if (this.tick !== null && normalizedTick <= this.tick) {
      throw new RangeError('multiplayer player-snake ticks must increase monotonically');
    }
    this.sync({ localPlayerId });
    if (!this.localPlayerId) return null;

    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld.snapshot();
    const snakeBackups = this.listSnakes().map((record) => ({
      playerId: record.playerId,
      local: record.local,
      snake: record.snake,
    }));

    try {
      const localInput = this.multiplayerState.getInputForTick(this.localPlayerId, normalizedTick).input;
      const localState = this.localSnakeState.snapshot();
      if (localState && localState.tick !== normalizedTick) {
        this.localSnakeState.step({
          tick: normalizedTick,
          simulationTime,
          stepSeconds: normalizedStepSeconds,
          input: localInput,
        });
      }
      const steppedPlayerIds = [this.localPlayerId];
      for (const [playerId, snakeState] of [...this.remoteSnakes.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        const input = this.multiplayerState.getInputForTick(playerId, normalizedTick).input;
        snakeState.step({
          tick: normalizedTick,
          simulationTime,
          stepSeconds: normalizedStepSeconds,
          input,
        });
        steppedPlayerIds.push(playerId);
      }
      this.tick = normalizedTick;
      this.revision += 1;
      this.lastStep = deepFreeze({
        tick: normalizedTick,
        simulationTime,
        stepSeconds: normalizedStepSeconds,
        steppedPlayerIds: steppedPlayerIds.sort(),
        state: this.getStateRef(),
      });
      return this.lastStep;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      this.traversalWorld.restore(traversalBackup);
      for (const backup of snakeBackups) {
        if (backup.local) this.localSnakeState.restore(backup.snake);
        else this.remoteSnakes.get(backup.playerId)?.restore(backup.snake);
      }
      this.entityWorld.restore(worldBackup);
      this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  getPlayerSnake(playerId) {
    const normalizedId = assertNonEmptyString(playerId, 'playerId');
    if (normalizedId === this.localPlayerId) return this.localSnakeState.snapshot();
    return this.remoteSnakes.get(normalizedId)?.snapshot() ?? null;
  }

  listSnakes() {
    if (!this.localPlayerId) return [];
    const players = this.multiplayerState.listPlayers();
    return players
      .filter((player) => player.status !== 'left')
      .map((player) => {
        const local = player.playerId === this.localPlayerId;
        const snake = local
          ? this.localSnakeState.snapshot()
          : this.remoteSnakes.get(player.playerId)?.snapshot() ?? null;
        return snake ? deepFreeze({
          playerId: player.playerId,
          displayName: player.displayName,
          role: player.role,
          playerStatus: player.status,
          local,
          snake,
        }) : null;
      })
      .filter(Boolean)
      .sort((left, right) => left.playerId.localeCompare(right.playerId));
  }

  snapshot() {
    const payload = {
      version: MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.tick,
      localPlayerId: this.localPlayerId,
      multiplayerChecksum: this.multiplayerState.snapshot().checksum,
      snakes: this.listSnakes().map((record) => clone(record)),
    };
    return deepFreeze({
      ...payload,
      checksum: computeMultiplayerPlayerSnakeChecksum(payload),
    });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION) {
      throw new RangeError(`Unsupported multiplayer player-snake snapshot version: ${parsed.version}`);
    }
    if (typeof parsed.checksum !== 'string'
      || parsed.checksum !== computeMultiplayerPlayerSnakeChecksum(parsed)) {
      throw new RangeError('snapshot checksum is invalid');
    }
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const tick = parsed.tick === null ? null : assertNonNegativeInteger(parsed.tick, 'snapshot.tick');
    const localPlayerId = parsed.localPlayerId === null
      ? null
      : assertNonEmptyString(parsed.localPlayerId, 'snapshot.localPlayerId');
    if (!Array.isArray(parsed.snakes)) throw new TypeError('snapshot.snakes must be an array');
    const records = parsed.snakes.map(normalizeRecord);
    if (new Set(records.map((record) => record.playerId)).size !== records.length) {
      throw new RangeError('snapshot contains duplicate player IDs');
    }
    if (records.filter((record) => record.local).length !== (localPlayerId === null ? 0 : 1)) {
      throw new RangeError('snapshot local player mapping is invalid');
    }
    if (records.some((record) => record.local && record.playerId !== localPlayerId)) {
      throw new RangeError('snapshot local player ID does not match the local snake');
    }

    const previous = this.snapshot();
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld.snapshot();
    try {
      for (const snakeState of this.remoteSnakes.values()) snakeState.reset({ removeEntity: true });
      this.remoteSnakes.clear();
      this.localPlayerId = localPlayerId;
      for (const record of records) {
        if (record.local) {
          const current = this.localSnakeState.snapshot();
          if (current && current.entityId !== record.snake.entityId) {
            this.localSnakeState.reset({ removeEntity: true });
          }
          this.localSnakeState.restore(record.snake);
        } else {
          const snakeState = new SnakeState({
            entityWorld: this.entityWorld,
            traversalWorld: this.traversalWorld,
          });
          snakeState.restore(record.snake);
          this.remoteSnakes.set(record.playerId, snakeState);
        }
      }
      this.revision = revision;
      this.tick = tick;
      this.lastStep = null;
      const restored = this.snapshot();
      if (restored.checksum !== parsed.checksum) throw new RangeError('snapshot state is not canonical');
      return restored;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      this.traversalWorld.restore(traversalBackup);
      this.remoteSnakes.clear();
      this.localPlayerId = previous.localPlayerId;
      for (const record of previous.snakes) {
        if (record.local) this.localSnakeState.restore(record.snake);
        else {
          const snakeState = new SnakeState({ entityWorld: this.entityWorld, traversalWorld: this.traversalWorld });
          snakeState.restore(record.snake);
          this.remoteSnakes.set(record.playerId, snakeState);
        }
      }
      this.revision = previous.revision;
      this.tick = previous.tick;
      this.entityWorld.restore(worldBackup);
      this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  reset({ preserveLocalSnake = true } = {}) {
    if (typeof preserveLocalSnake !== 'boolean') {
      throw new TypeError('preserveLocalSnake must be a boolean');
    }
    for (const [playerId, snakeState] of this.remoteSnakes.entries()) {
      const snake = snakeState.snapshot();
      if (snake) this.#release(playerId, snake.entityId);
      snakeState.reset({ removeEntity: true });
    }
    this.remoteSnakes.clear();
    const localSnake = this.localSnakeState.snapshot();
    if (this.localPlayerId && localSnake) this.#release(this.localPlayerId, localSnake.entityId);
    if (!preserveLocalSnake) this.localSnakeState.reset({ removeEntity: true });
    this.localPlayerId = null;
    this.tick = null;
    this.lastStep = null;
    this.revision += 1;
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      active: snapshot.snakes.length > 0,
      localPlayerId: snapshot.localPlayerId,
      snakeCount: snapshot.snakes.length,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: MULTIPLAYER_PLAYER_SNAKE_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.tick,
      active: this.localPlayerId !== null,
      localPlayerId: this.localPlayerId,
      snakeCount: this.listSnakes().length,
      remoteSnakeCount: this.remoteSnakes.size,
      checksum: this.snapshot().checksum,
    });
  }

  getDiagnostics() {
    return deepFreeze({
      status: this.getStatus(),
      lastStep: this.lastStep,
      snakes: this.listSnakes(),
    });
  }
}
